import type {AppRouteMeta} from "../../../bootstrap/router/route-meta";
import type {ProjectMessageDescriptor} from "@project/i18n";

import {useI18n} from "@project/i18n/react";
import {useMatches} from "@tanstack/react-router";

export interface Crumb {
  id: string;
  label: string;
  pathname: string;
  /** When true, this breadcrumb is not navigable (e.g., module namespace items). */
  isModule?: boolean;
}

/**
 * useBreadcrumbs — derives breadcrumb trail from matched route metadata.
 *
 * Resolution order:
 *   - route label: `getLabel` → `getLabelDescriptor` → `labelDescriptor` → `label`
 *   - module label: `moduleDescriptor` → `module`
 *   - `breadcrumbs.hide` skips route entirely
 */
export function useBreadcrumbs(): Crumb[] {
  const {intl, t} = useI18n();
  const matches = useMatches();

  const crumbs: Crumb[] = [];
  const seenModules = new Set<string>();

  matches.forEach((match) => {
    const meta: AppRouteMeta | undefined = match.staticData?.meta;

    if (!meta) return;
    if (meta.breadcrumbs?.hide) return;

    const moduleLabel = meta.moduleDescriptor
      ? intl.formatMessage({id: meta.moduleDescriptor.id}, meta.moduleDescriptor.values)
      : (meta.module ?? null);

    // Inject module breadcrumb if present and not already added
    if (moduleLabel && !seenModules.has(moduleLabel)) {
      seenModules.add(moduleLabel);
      crumbs.push({
        id: `module-${moduleLabel}`,
        label: moduleLabel,
        pathname: match.pathname,
        isModule: true,
      });
    }

    const params = match.params as Record<string, string>;
    const dynamicLabel = meta.getLabel?.(params, match.loaderData);
    const dynamicDescriptor = meta.getLabelDescriptor?.(params, match.loaderData);

    const label =
      dynamicLabel ??
      (dynamicDescriptor
        ? intl.formatMessage({id: dynamicDescriptor.id}, dynamicDescriptor.values)
        : meta.labelDescriptor
          ? t(meta.labelDescriptor as ProjectMessageDescriptor, meta.labelDescriptor.values)
          : (meta.label ?? null));

    const parent = meta.breadcrumbs?.getParent?.(params, match.loaderData);
    if (parent) crumbs.push({id: `${match.id}-resource`, ...parent});
    if (label) {
      crumbs.push({id: match.id, label, pathname: match.pathname});
    }
  });

  return crumbs;
}
